/* eslint-disable local/no-conditional-class-names */

import type { InputProps as ChakraInputProps } from '@chakra-ui/react';
import {
  Input as ChakraInput,
  InputGroup,
  InputLeftElement,
  InputRightElement,
} from '@chakra-ui/react';
import classnames from 'classnames';
import React from 'react';

import type { OmitChakraProps } from '../../utils/typeUtils';
import { withChakraAs } from '../../utils/withChakraAs';

export interface InputProps
  extends OmitChakraProps<
    ChakraInputProps,
    'disabled' | 'required' | 'isRequired'
  > {
  /**
   * Class name to apply to the input element
   */
  inputClassName?: string;
  /**
   * Elements meant to be displayed internally on the left side of the input
   */
  leftElement?: React.ReactNode;
  /**
   * Elements meant to be displayed internally on the right side of the input
   */
  rightElement?: React.ReactNode;
}

export const Input = withChakraAs<InputProps, 'input'>(
  ({
    leftElement,
    rightElement,
    className,
    inputClassName,
    size,
    variant,
    ...props
  }) => {
    return (
      // `size` and `variant` belong on InputGroup, not the inner field.
      // Chakra clones direct children with these props, so this is what
      // makes `InputLeftElement` / `InputRightElement` actually resolve
      // their size-specific theme styles. Without this, the elements
      // always render at the default `md` (40×40) regardless of the
      // field's size, which mis-aligns icons in `xs`/`sm`/`lg` inputs.
      <InputGroup
        size={size}
        variant={variant}
        className={classnames('isolate', className)}
      >
        {!!leftElement && (
          <InputLeftElement
            className={classnames(props.isDisabled ? 'text-icon-disabled' : '')}
          >
            {leftElement}
          </InputLeftElement>
        )}
        <ChakraInput {...props} className={inputClassName} />
        {!!rightElement && (
          <InputRightElement
            className={classnames(props.isDisabled ? 'text-icon-disabled' : '')}
          >
            {rightElement}
          </InputRightElement>
        )}
      </InputGroup>
    );
  },
);
